import type { SceneBuilder } from "../../reel/scenes.js";
import { brandBackdrop } from "../../primitives/brandBackdrop.js";
import {
  accentFor, clipFrame, clipOutroFade, combineSceneFragments, makeTl, typeSize, weights, withAlpha,
  colors as themeColors, escapeHtml, gsapSetLine, brandName, brandTagline,
} from "../../reel/sceneShared.js";

/**
 * Path draw mark — uses SVG stroke-dasharray draw-in. Replaces `@remotion/paths`
 * `evolvePath`+`getLength` with a fixed dashLen long enough to cover both presets.
 */
export const pathDraw: SceneBuilder = ({ ctx, clipPrefix, inputs, clipDurationSec, clipStartSec }) => {
  const variant = inputs.variant ?? "monogram";
  const headline = inputs.text ?? brandTagline ?? brandName;
  const sub = inputs.subtext ?? "";
  const accent = accentFor(inputs);
  const initial = String(inputs.extras?.initial ?? brandName.charAt(0).toUpperCase());

  const lg = typeSize("lg", ctx.format);
  const VBOX = 600;
  const path = variant === "wave"
    ? "M 40 320 C 120 180, 220 460, 320 320 S 520 180, 600 320 L 600 460 L 40 460 Z"
    : "M 300 90 A 210 210 0 1 1 299.99 90 M 180 470 L 420 130";
  const dashLen = 3500;

  const t = makeTl(clipStartSec);
  const svgSize = ctx.format === "9:16" ? 460 : 380;
  const fillId = `${clipPrefix}-pathfill`;
  const strokeId = `${clipPrefix}-pathstroke`;
  const letterId = `${clipPrefix}-letter`;
  const headId = `${clipPrefix}-head`;
  const subId = `${clipPrefix}-sub`;

  const backdrop = brandBackdrop(ctx, `${clipPrefix}-bg`, { variant: "cinematic", accent });

  const inner = `<div style="width:${svgSize}px;height:${svgSize}px;position:relative;">
  <svg viewBox="0 0 ${VBOX} ${VBOX}" width="100%" height="100%" style="filter:drop-shadow(0 0 30px ${withAlpha(accent, 0.5)});">
    <path id="${fillId}" d="${path}" fill="${withAlpha(accent, 0.15)}" stroke="none" />
    <path id="${strokeId}" d="${path}" fill="none" stroke="${accent}" stroke-width="10" stroke-linecap="round" stroke-linejoin="round" stroke-dasharray="${dashLen}" stroke-dashoffset="${dashLen}" />
    ${variant === "monogram" ? `<text id="${letterId}" x="${VBOX / 2}" y="${VBOX / 2 + 40}" font-size="220" font-family="Outfit,sans-serif" font-weight="${weights.black}" fill="${themeColors.fg}" text-anchor="middle">${escapeHtml(initial)}</text>` : ""}
  </svg>
</div>
${headline ? `<h1 id="${headId}" style="margin:0;font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${lg * 0.7}px;color:${themeColors.fg};letter-spacing:-2px;line-height:1.05;text-align:center;text-wrap:balance;max-width:${ctx.format === "9:16" ? 760 : 1100}px;">${escapeHtml(headline)}</h1>` : ""}
${sub ? `<div id="${subId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.semibold};font-size:${lg * 0.34}px;color:${withAlpha(themeColors.fg, 0.85)};text-align:center;letter-spacing:-0.4px;max-width:800px;">${escapeHtml(sub)}</div>` : ""}`;

  const html = clipFrame({ clipPrefix, clipStartSec, clipDurationSec, inner, gapPx: 40 });

  const drawDurSec = 1.4;

  const initLines = [
    gsapSetLine(`#${fillId}`, { opacity: 0 }),
    variant === "monogram" ? gsapSetLine(`#${letterId}`, { opacity: 0 }) : "",
    headline ? gsapSetLine(`#${headId}`, { opacity: 0, y: 14 }) : "",
    sub ? gsapSetLine(`#${subId}`, { opacity: 0, y: 14 }) : "",
  ].filter(Boolean).join("\n");

  const tweens: string[] = [
    t.to(`#${strokeId}`, { attr: { "stroke-dashoffset": 0 }, duration: drawDurSec, ease: "raw:\"expo.out\"" }, 4 / 30),
    t.to(`#${fillId}`, { opacity: 1, duration: 0.6, ease: "raw:\"power1.out\"" }, (4 / 30) + drawDurSec - 4 / 30),
  ];
  if (variant === "monogram") {
    tweens.push(t.fromTo(`#${letterId}`, { opacity: 0, scale: 0.6 }, { opacity: 1, scale: 1, duration: 0.47, ease: "raw:\"back.out(1.4)\"", transformOrigin: "50% 50%" }, (4 / 30) + drawDurSec + 4 / 30));
  }
  if (headline) tweens.push(t.to(`#${headId}`, { opacity: 1, y: 0, duration: 0.6, ease: "raw:\"power1.out\"" }, (4 / 30) + drawDurSec + 14 / 30));
  if (sub) tweens.push(t.to(`#${subId}`, { opacity: 1, y: 0, duration: 0.53, ease: "raw:\"power1.out\"" }, (4 / 30) + drawDurSec + 26 / 30));
  tweens.push(clipOutroFade(clipPrefix, clipDurationSec, clipStartSec, 12));

  return combineSceneFragments([backdrop, { html, init: initLines, tweens }]);
};
